// src/pages/Conta/OrdersTab.jsx
import { useEffect, useMemo, useState } from "react";
import { API_BASE, apiGet } from "../../lib/api.js";
import StatusBadge from "../../components/StatusBadge";

function Pager({ page, pages, onGo }) {
  if (pages <= 1) return null;
  return (
    <nav>
      <ul className="pagination justify-content-center">
        <li className={`page-item ${page <= 1 ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onGo(page - 1)}>
            «
          </button>
        </li>
        {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
          <li key={p} className={`page-item ${p === page ? "active" : ""}`}>
            <button className="page-link" onClick={() => onGo(p)}>
              {p}
            </button>
          </li>
        ))}
        <li className={`page-item ${page >= pages ? "disabled" : ""}`}>
          <button className="page-link" onClick={() => onGo(page + 1)}>
            »
          </button>
        </li>
      </ul>
    </nav>
  );
}

export default function OrdersTab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState(null); // encomenda selecionada

  const pages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total]);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      const data = await apiGet(`/api/me/orders`);
      const arr = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      const filtered = status ? arr.filter((o) => o.status === status) : arr;
      setItems(filtered);
      setTotal(filtered.length);
    } catch (e) {
      setErr("Não foi possível carregar as encomendas.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status, page]);

  return (
    <div>
      <div className="d-flex gap-2 align-items-center mb-3">
        <h5 className="mb-0">As minhas encomendas</h5>
        <div className="ms-auto d-flex gap-2">
          <select
            className="form-select"
            style={{ maxWidth: 240 }}
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
          >
            <option value="">Todos os estados</option>
            <option value="pending">Pendente</option>
            <option value="paid">Paga</option>
            <option value="failed">Falhada</option>
            <option value="expired">Expirada</option>
            <option value="refunded">Reembolsada</option>
          </select>
        </div>
      </div>

      {loading && <div className="py-4">A carregar…</div>}
      {err && <div className="text-danger py-2">{err}</div>}

      {!loading && !err && (
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                <th>Data</th>
                <th>Ref.</th>
                <th>Estado</th>
                <th>Total</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => (
                <tr key={o._id}>
                  <td>
                    {o.createdAt
                      ? new Date(o.createdAt).toLocaleString()
                      : "-"}
                  </td>
                  <td>{o.orderRef}</td>
                  <td>
                    <StatusBadge status={o.status} />
                  </td>
                  <td>{(o.totals?.totalCVE || 0).toLocaleString()} CVE</td>
                  <td className="text-end">
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setSel(o)}
                    >
                      Ver detalhes
                    </button>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-4">
                    Sem encomendas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex justify-content-center">
        <Pager page={page} pages={pages} onGo={setPage} />
      </div>

      {sel && <OrderDetailsModal order={sel} onClose={() => setSel(null)} />}
    </div>
  );
}

/* =================== Modal de Detalhes =================== */
function OrderDetailsModal({ order, onClose }) {
  const [working, setWorking] = useState(false);
  const [dlUrl, setDlUrl] = useState("");
  const [err, setErr] = useState("");

  // ✅ Corrigido: define isPaid
  const isPaid =
    order.status === "paid" ||
    order.status === "pago" ||
    order.payment?.status === "paid" ||
    order.payment?.status === "pago";

  async function gerarDownload() {
    try {
      setErr("");
      setDlUrl("");

      if (!isPaid) {
        setErr(
          "O link de download ficará disponível assim que o pagamento for confirmado."
        );
        return;
      }

      setWorking(true);
      const r = await apiGet(
        `/api/download/by-ref/${encodeURIComponent(order.orderRef)}`
      );

      const url = r?.downloads?.[0]?.url || r?.url || null;
      if (!url) throw new Error("sem url");
      setDlUrl(url);
    } catch (e) {
      console.error(e);
      setErr("Não foi possível obter o link de download neste momento.");
    } finally {
      setWorking(false);
    }
  }

  async function obterRecibo() {
    try {
      setErr("");
      setDlUrl("");

      if (!isPaid) {
        setErr(
          "O recibo em PDF ficará disponível assim que o pagamento for confirmado."
        );
        return;
      }

      setWorking(true);
      const r = await apiGet(
        `/api/orders/${encodeURIComponent(order.orderRef)}/receipt`
      );

      if (!r?.pdfUrl) throw new Error("sem pdfUrl");
      setDlUrl(r.pdfUrl);
    } catch (e) {
      console.error(e);
      setErr("Não foi possível abrir o recibo neste momento.");
    } finally {
      setWorking(false);
    }
  }

  function abrirFatura() {
    setErr("");
    setDlUrl("");

    if (!isPaid) {
      setErr(
        "A fatura em PDF ficará disponível assim que o pagamento for confirmado."
      );
      return;
    }

    window.open(
      `${API_BASE}/api/orders/${encodeURIComponent(
        order.orderRef
      )}/invoice.pdf`,
      "_blank",
      "noopener,noreferrer"
    );
  }

  return (
    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
      <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              Encomenda {order.orderRef}{" "}
              <small className="ms-2">
                <StatusBadge status={order.status} />
              </small>
            </h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="row g-3">
              <div className="col-md-6">
                <h6>Itens</h6>
                <ul className="list-group">
                  {(order.items || []).map((it) => (
                    <li
                      key={it.sku}
                      className="list-group-item d-flex align-items-center gap-2"
                    >
                      {it.coverImage ? (
                        <img
                          src={it.coverImage}
                          alt=""
                          width={44}
                          height={60}
                          style={{ objectFit: "cover" }}
                        />
                      ) : null}
                      <div className="flex-grow-1">
                        <div className="fw-semibold">
                          {it.title || it.name}
                        </div>
                        <div className="small text-muted">
                          SKU: {it.sku} • Qtd: {it.qty}
                        </div>
                      </div>
                      {it.isDigital ? (
                        <span className="badge text-bg-success">Digital</span>
                      ) : (
                        <span className="badge text-bg-light">Físico</span>
                      )}
                    </li>
                  ))}
                </ul>

                {working && <div className="mt-3">A processar…</div>}
                {err && <div className="text-danger mt-2">{err}</div>}
                {dlUrl && (
                  <div className="alert alert-success mt-3">
                    Link:{" "}
                    <a href={dlUrl} target="_blank" rel="noreferrer">
                      Abrir
                    </a>
                    <div className="small text-muted">
                      (Links de download expiram em 48h / 1 utilização;
                      recibo permanece acessível.)
                    </div>
                  </div>
                )}
              </div>

              <div className="col-md-6">
                <h6>Totais</h6>
                <ul className="list-group mb-3">
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Subtotal</span>
                    <strong>
                      {(order.totals?.subtotalCVE || 0).toLocaleString()} CVE
                    </strong>
                  </li>
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Portes</span>
                    <strong>
                      {(order.totals?.shippingCVE || 0).toLocaleString()} CVE
                    </strong>
                  </li>
                  {order.totals?.discountCVE ? (
                    <li className="list-group-item d-flex justify-content-between">
                      <span>Desconto</span>
                      <strong>
                        -
                        {(order.totals?.discountCVE || 0).toLocaleString()} CVE
                      </strong>
                    </li>
                  ) : null}
                  <li className="list-group-item d-flex justify-content-between">
                    <span>Total</span>
                    <strong>
                      {(order.totals?.totalCVE || 0).toLocaleString()} CVE
                    </strong>
                  </li>
                </ul>

                <h6>Pagamento</h6>
                <div className="border rounded p-2">
                  <div>
                    Provedor:{" "}
                    <strong>{order.payment?.provider || "-"}</strong>
                  </div>
                  <div>
                    Estado:{" "}
                    <StatusBadge status={order.payment?.status || "pending"} />
                  </div>
                  <div>
                    Pago em:{" "}
                    {order.payment?.paidAt
                      ? new Date(order.payment.paidAt).toLocaleString()
                      : "-"}
                  </div>
                </div>

                <div className="d-flex gap-2 mt-3">
                  <button
                    className="btn btn-outline-success"
                    onClick={gerarDownload}
                    disabled={working || !isPaid}
                    title={
                      isPaid
                        ? "Gerar link de download"
                        : "Disponível após confirmação do pagamento."
                    }
                  >
                    Gerar link de download
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={obterRecibo}
                    disabled={working || !isPaid}
                    title={
                      isPaid
                        ? "Ver recibo em PDF"
                        : "Disponível após confirmação do pagamento."
                    }
                  >
                    Ver recibo (PDF)
                  </button>
                  <button
                    className="btn btn-outline-dark"
                    onClick={abrirFatura}
                    disabled={working || !isPaid}
                    title={
                      isPaid
                        ? "Abrir fatura em PDF"
                        : "A fatura será disponibilizada após confirmação do pagamento."
                    }
                  >
                    Fatura (PDF)
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
